import { CSSObject, Theme } from '@emotion/react';
import * as zIndex from 'styles/zIndex';

const DRAWER_WIDTH = 440;

const page: CSSObject = {
  paddingBottom: 40,
};

const boxHead: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
};

const publishRow = (theme: Theme): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0 32px',
  fontSize: 14,
  '> p > span': {
    color: theme.color.grey500,
    marginRight: 12,
  },
});

const stateBadge =
  (published: boolean) =>
  (theme: Theme): CSSObject => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    color: published ? theme.color.white : theme.color.grey600,
    backgroundColor: published ? theme.color.success : theme.color.grey75,
  });

const columns: CSSObject = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0 12px',
  '> div:first-of-type': { flex: 1, minWidth: 0 },
  '> div:last-of-type': { width: 360, flexShrink: 0 },
};

const tabs = (theme: Theme): CSSObject => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  borderBottom: `1px solid ${theme.color.border}`,
});

const tab =
  (active: boolean) =>
  (theme: Theme): CSSObject => ({
    padding: '10px 14px',
    fontSize: 13,
    whiteSpace: 'pre',
    cursor: 'pointer',
    borderBottom: `2px solid ${active ? theme.color.black : 'transparent'}`,
    fontWeight: active ? 700 : 400,
    color: active ? theme.color.black : theme.color.grey500,
  });

const table = (theme: Theme): CSSObject => ({
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 8,
  'th, td': {
    padding: '12px 8px',
    borderBottom: `1px solid ${theme.color.border}`,
    fontSize: 13,
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  th: {
    background: '#fafafa',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
});

const groupedRow: CSSObject = {
  backgroundColor: '#f5f7fa',
  '> td:first-of-type': { boxShadow: 'inset 3px 0 0 #9AA5B1' },
};

const manageCell: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
};

const orderButton = (theme: Theme): CSSObject => ({
  width: 24,
  height: 24,
  border: `1px solid ${theme.color.border}`,
  borderRadius: 3,
  background: theme.color.white,
  cursor: 'pointer',
  '&:disabled': {
    cursor: 'not-allowed',
    color: theme.color.grey200,
    background: theme.color.grey50,
  },
});

const phone = (theme: Theme): CSSObject => ({
  border: `1px solid ${theme.color.grey100}`,
  borderRadius: 16,
  overflow: 'hidden',
  background: theme.color.grey50,
  minHeight: 480,
});

const phoneTabs = (theme: Theme): CSSObject => ({
  display: 'flex',
  gap: 6,
  overflowX: 'auto',
  padding: 12,
  background: theme.color.white,
  borderBottom: `1px solid ${theme.color.grey75}`,
});

const phoneTab =
  (active: boolean) =>
  (theme: Theme): CSSObject => ({
    flexShrink: 0,
    padding: '6px 10px',
    borderRadius: 14,
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: active ? theme.color.white : theme.color.grey600,
    backgroundColor: active ? theme.color.grey900 : theme.color.grey75,
  });

const timeline: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
};

const card =
  (isDraft: boolean) =>
  (theme: Theme): CSSObject => ({
    display: 'flex',
    gap: 12,
    padding: 12,
    borderRadius: 10,
    background: theme.color.white,
    border: `1px ${isDraft ? 'dashed' : 'solid'} ${
      isDraft ? theme.color.lightBlue1 : theme.color.grey75
    }`,
    '> p:first-of-type': {
      width: 64,
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 600,
      color: theme.color.grey700,
      whiteSpace: 'pre-line',
    },
    '> div': { flex: 1, minWidth: 0 },
    '> div > p:first-of-type': { fontSize: 14, fontWeight: 600 },
    '> div > p:nth-of-type(2)': {
      marginTop: 4,
      fontSize: 12,
      color: theme.color.grey500,
    },
  });

const drawerBackdrop: CSSObject = {
  position: 'fixed',
  inset: 0,
  zIndex: zIndex.DRAWER,
  background: 'rgba(0, 0, 0, 0.4)',
};

const drawer = (theme: Theme): CSSObject => ({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: DRAWER_WIDTH,
  zIndex: zIndex.DRAWER,
  display: 'flex',
  flexDirection: 'column',
  background: theme.color.white,
  boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)',
});

const drawerHead = (theme: Theme): CSSObject => ({
  padding: '20px',
  fontSize: 16,
  fontWeight: 600,
  borderBottom: `1px solid ${theme.color.grey75}`,
});

const drawerBody: CSSObject = {
  flex: 1,
  overflowY: 'auto',
  padding: '16px 20px',
};

const drawerFoot = (theme: Theme): CSSObject => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: '12px 20px',
  borderTop: `1px solid ${theme.color.grey75}`,
  '> button': { minWidth: 80, height: 36 },
});

const field = (theme: Theme): CSSObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 16,
  fontSize: 13,
  '> label': { fontWeight: 600 },
  'input, select, textarea': { width: '100%', boxSizing: 'border-box' },
  '> small': { color: theme.color.grey500, fontSize: 12 },
});

const error = (theme: Theme): CSSObject => ({
  color: theme.color.error,
  fontSize: 12,
});

const note = (theme: Theme): CSSObject => ({
  marginTop: 8,
  fontSize: 12,
  color: theme.color.grey500,
});

export {
  page,
  boxHead,
  publishRow,
  stateBadge,
  columns,
  tabs,
  tab,
  table,
  groupedRow,
  manageCell,
  orderButton,
  phone,
  phoneTabs,
  phoneTab,
  timeline,
  card,
  drawerBackdrop,
  drawer,
  drawerHead,
  drawerBody,
  drawerFoot,
  field,
  error,
  note,
};
